/**
 *
 * Recipe Page
 *
 */
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  Dropdown,
  Menu,
  notification,
  PageHeader,
  Modal,
} from 'antd';
import { useSelector } from 'react-redux';
import { generatePath, RouteComponentProps, useHistory } from 'react-router';

import { RouteConstants } from 'app/routes';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { MainContainer } from 'app/components/LayoutComponents/LayoutComponents';
import { useArchiveRecipeMutation, useGetRecipeByIdQuery } from 'app/api';
import { EllipsisIcon } from 'app/components/CommonIcons/CommonIcons';
import { handleError } from 'app/containers/Global/slice/helpers';
import RecipeDetails from '../../parts/RecipeDetails/RecipeDetails';
import { useResponsiveQuery } from 'utils/responsiveQuery/useResponsiveQuery';

interface IReactRouterParams {
  recipeId: string;
}

interface IProps extends RouteComponentProps<IReactRouterParams> {}

const RecipePage: FC<IProps> = ({ match: { params } }) => {
  const recipeId = Number(params.recipeId);

  const { t } = useTranslation();
  const history = useHistory();
  const { mobileOrTablet } = useResponsiveQuery();

  const envId = useSelector(selectSelectedEnvId) as number;

  const [
    archiveRecipe,
    { isLoading: isArchiveLoading },
  ] = useArchiveRecipeMutation();

  const { data: recipe, isFetching } = useGetRecipeByIdQuery(
    { envId, recipeId },
    { skip: !recipeId },
  );

  const goBackToList = () => {
    history.push(
      generatePath(RouteConstants.recipe.index, {
        envId,
      }),
    );
  };

  const onArchive = () => {
    Modal.confirm({
      title: t(
        'Recipe.RecipeArchiveConfirm',
        'Are you sure you want to archive this recipe?',
      ),
      onOk: onArchiveConfirm,
      okText: t('Common.Yes', 'Yes'),
      cancelText: t('Common.No', 'No'),
    });
  };

  const onArchiveConfirm = async () => {
    try {
      await archiveRecipe({
        envId,
        recipeId,
      }).unwrap();

      goBackToList();

      notification.success({
        message: t('Recipe.ArchivedSuccessfully', {
          defaultValue: 'Recipe "{{name}}" archived successfully',
          name: recipe?.name,
        }),
        placement: 'bottomRight',
      });
    } catch (error) {
      handleError(error);
    }
  };

  const onEdit = () => {
    history.push(
      generatePath(RouteConstants.recipe.edit, {
        envId,
        recipeId,
      }),
    );
  };

  return (
    <>
      <PageHeader
        onBack={goBackToList}
        className="page-header"
        title={recipe?.name}
      />
      <MainContainer>
        <Card
          loading={isFetching || isArchiveLoading}
          style={{
            minHeight: mobileOrTablet ? 'unset' : 'calc(100vh - 190px)',
            maxHeight: mobileOrTablet ? 'unset' : 'calc(100vh - 170px)',
            overflow: 'auto',
            width: '100%',
          }}
          title={t('Recipe.RecipeInformation', 'Recipe information')}
          extra={[
            <Dropdown
              trigger={['click']}
              key="dropdown"
              overlay={
                <Menu>
                  <Menu.Item onClick={onEdit}>
                    {t('Common.Edit', 'Edit')}
                  </Menu.Item>
                  <Menu.Item onClick={onArchive}>
                    {t('Common.Archive', 'Archive')}
                  </Menu.Item>
                </Menu>
              }
            >
              <Button type="primary" shape="circle">
                <EllipsisIcon size={14} />
              </Button>
            </Dropdown>,
          ]}
        >
          <RecipeDetails recipe={recipe} />
        </Card>
      </MainContainer>
    </>
  );
};

export default RecipePage;
